import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { X, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Floating WhatsApp CTA — visible on every public page.
 *
 * Flow: user clicks the pill → mini-form modal (Name / Email / Phone / note)
 * → submit posts to /api/leads/whatsapp → backend logs a lead + fans an alert
 * out to every active admin/architect → response returns a wa.me deep link
 * that we open in a new tab.
 *
 * We deliberately hide the widget on authenticated Studio routes and on
 * login/auth-callback so it doesn't distract internal users.
 */
export default function WhatsAppCTA() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  // Don't render on Studio / login / auth flows
  const path = location.pathname || '/';
  const hiddenPrefixes = ['/admin', '/login'];
  if (hiddenPrefixes.some((p) => path === p || path.startsWith(`${p}/`))) return null;
  if (location.hash?.includes('session_id=')) return null;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openModal = () => {
    setOpen(true);
    // Fire-and-forget click impression so the studio can see the true funnel
    // (button clicks vs form submissions). Never blocks the modal open.
    api.post('/leads/whatsapp/impression', {}).catch(() => {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.email.trim() && !form.phone.trim()) {
      toast.error('Please share a name, email, or phone number.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/leads/whatsapp', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      toast.success("Opening WhatsApp — we've noted your details.");
      // Open WhatsApp in a new tab; if popups are blocked, keep the modal open
      // with a manual link so nobody hits a dead-end.
      const win = window.open(data.deep_link, '_blank', 'noopener,noreferrer');
      if (!win) {
        window.location.href = data.deep_link;
      }
      setOpen(false);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open WhatsApp. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating pill button */}
      <button
        type="button"
        onClick={openModal}
        aria-label="Chat on WhatsApp"
        data-testid="whatsapp-cta-button"
        className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#20BA5A] text-white px-4 py-3 shadow-lg shadow-black/20 transition-transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2 focus:ring-offset-transparent"
      >
        <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true" className="flex-shrink-0">
          <path fill="currentColor" d="M16 3C9.373 3 4 8.373 4 15c0 2.36.696 4.552 1.895 6.406L4 29l7.83-1.855A11.94 11.94 0 0 0 16 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 22a9.94 9.94 0 0 1-5.09-1.39l-.365-.216-4.65 1.102 1.13-4.529-.238-.372A9.96 9.96 0 0 1 6 15c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10zm5.516-7.478c-.302-.151-1.784-.881-2.06-.982-.277-.101-.478-.15-.678.152-.202.302-.777.98-.953 1.181-.176.201-.352.226-.653.075-.302-.151-1.275-.47-2.428-1.5-.898-.802-1.505-1.792-1.681-2.094-.176-.302-.019-.465.133-.616.137-.135.302-.352.453-.528.15-.176.202-.302.302-.503.101-.201.05-.377-.025-.528-.075-.151-.678-1.636-.929-2.24-.244-.586-.492-.507-.678-.516l-.578-.01c-.201 0-.528.075-.804.377-.277.302-1.055 1.031-1.055 2.516s1.08 2.917 1.23 3.118c.15.201 2.126 3.247 5.155 4.552.72.311 1.281.497 1.72.635.723.23 1.381.198 1.9.12.58-.087 1.784-.729 2.037-1.435.252-.706.252-1.31.176-1.435-.075-.126-.276-.201-.578-.352z"/>
        </svg>
        <span className="text-sm font-body font-medium hidden sm:inline">Chat on WhatsApp</span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          data-testid="whatsapp-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-sm border border-black/10 overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/10 bg-[#075E54] text-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center">
                  <MessageCircle className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-sm font-body font-medium leading-tight">Chat on WhatsApp</p>
                  <p className="text-[11px] font-body opacity-80 leading-tight">Raum Architects — replies within 24 hrs</p>
                </div>
              </div>
              <button type="button" onClick={() => setOpen(false)} data-testid="whatsapp-modal-close"
                className="text-white/80 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-3.5" data-testid="whatsapp-lead-form">
              <p className="text-xs font-body text-brand-dark/60 leading-relaxed">
                Share a few details so we can pick up quickly. We&apos;ll open WhatsApp with your message pre-filled.
              </p>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="Your full name"
                  className="rounded-sm h-10 font-body text-sm"
                  data-testid="whatsapp-name-input"
                  autoFocus
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => update({ email: e.target.value })}
                  placeholder="you@example.com"
                  className="rounded-sm h-10 font-body text-sm"
                  data-testid="whatsapp-email-input"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">Phone</label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => update({ phone: e.target.value })}
                  placeholder="+27 ..."
                  className="rounded-sm h-10 font-body text-sm"
                  data-testid="whatsapp-phone-input"
                />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-brand-dark/60 block mb-1">
                  What would you like to chat about? <span className="text-brand-dark/30 normal-case tracking-normal font-normal">(optional)</span>
                </label>
                <Textarea
                  value={form.message}
                  onChange={(e) => update({ message: e.target.value })}
                  placeholder="A quick note about your project…"
                  className="rounded-sm font-body text-sm min-h-[80px]"
                  data-testid="whatsapp-message-input"
                />
              </div>

              <div className="pt-1">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="whatsapp-submit-button"
                  className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white rounded-sm h-11 font-body text-xs uppercase tracking-wider"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Open WhatsApp'}
                </Button>
                <p className="text-[10px] font-body text-brand-dark/40 text-center mt-2">
                  By continuing you agree that Raum may contact you about your enquiry.
                </p>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
